import React from 'react';
import { motion } from 'framer-motion';
import { FileText, Users, Heart, AlertCircle, Scale, CheckCircle } from 'lucide-react';
import PageHeader from '../../components/UI/PageHeader';
import Container from '../../components/UI/Container';
import SEOHead from '../../components/SEO/SEOHead';
import { useSystemSettings } from '../../contexts/SystemSettingsContext';

const TermsConditions: React.FC = () => {
  const { settings } = useSystemSettings();

  const sections = [
    {
      icon: <FileText className="w-6 h-6" />,
      title: "1. Introducere și definiții",
      content: `Acești termeni reglementează utilizarea site-ului Protecția Animalelor Sector 1, administrat în cadrul Sectorului 1 al Municipiului București - Primăria Sectorului 1.

În sensul acestor termeni:
• Site - platforma web disponibilă publicului pentru informare, adopții, contact și servicii conexe protecției animalelor;
• Administrator - Sectorul 1 al Municipiului București - Primăria Sectorului 1, prin structurile sale de specialitate;
• Utilizator - orice persoană fizică sau juridică ce accesează sau folosește site-ul;
• Servicii - funcționalitățile disponibile prin site, inclusiv consultarea animalelor disponibile, transmiterea formularelor și accesarea informațiilor publice.`
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "2. Scopul și funcționalitățile site-ului",
      content: `Site-ul este destinat informării publicului și facilitării accesului la servicii digitale legate de protecția animalelor.

Prin intermediul site-ului, utilizatorii pot:
• Vizualizarea animalelor disponibile pentru adopție
• Accesarea informațiilor despre activitatea de protecție a animalelor
• Transmiterea de mesaje sau solicitări prin formulare de contact
• Completarea formularelor de adopție, acolo unde această funcționalitate este disponibilă
• Descărcarea unor documente publice sau formulare
• Accesarea politicilor publicate în footer, inclusiv confidențialitate, cookie-uri și accesibilitate

Accesarea site-ului este gratuită. Eventualele costuri asociate unor proceduri administrative, dacă există, sunt stabilite potrivit reglementărilor aplicabile și nu prin acești termeni.`
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: "3. Acceptarea termenilor",
      content: `Prin accesarea și utilizarea site-ului, utilizatorul confirmă că a luat cunoștință de acești termeni și îi acceptă. Dacă nu sunteți de acord cu oricare dintre prevederi, vă rugăm să nu utilizați funcționalitățile site-ului care presupun transmiterea de date sau solicitări.

Utilizarea efectivă a site-ului, transmiterea unui formular sau continuarea navigării reprezintă acceptarea acestor reguli în forma publicată la momentul utilizării.`
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "4. Condiții de utilizare și obligațiile utilizatorului",
      content: `Utilizatorul se obligă să folosească site-ul cu bună-credință și potrivit legii.

Nu este permisă utilizarea site-ului pentru:
• Activități ilegale sau frauduloase
• Transmiterea de viruși, cod malițios sau conținut care afectează funcționarea site-ului
• Furnizarea intenționată de date false sau documente neconforme
• Încălcarea drepturilor de autor, a drepturilor de imagine sau a altor drepturi ale terților
• Transmiterea de mesaje abuzive, discriminatorii, amenințătoare sau neadecvate
• Comercializarea neautorizată de animale sau promovarea unor practici contrare protecției animalelor

Utilizatorul este responsabil pentru exactitatea informațiilor furnizate în formulare și pentru actualizarea lor atunci când situația comunicată se schimbă.`
    },
    {
      icon: <Heart className="w-6 h-6" />,
      title: "5. Informații privind adopția",
      content: `Informațiile despre animalele disponibile pentru adopție sunt publicate cu scop informativ și pot fi actualizate în funcție de situația reală a fiecărui animal.

Procesul de adopție poate presupune, după caz:
• Vârsta minimă de 18 ani
• Completarea unui formular sau transmiterea unei solicitări
• Prezentarea actului de identitate și a informațiilor necesare verificării
• Analizarea compatibilității dintre adoptator, locuință și animal
• Acceptarea obligațiilor asumate prin documentele specifice adopției
• Respectarea legislației privind protecția animalelor

Responsabilitățile adoptatorului:
• Îngrijirea corespunzătoare a animalului
• Asigurarea tratamentului veterinar
• Respectarea legilor locale privind animalele
• Informarea adăpostului în caz de probleme
• Returnarea sau sesizarea instituției dacă animalul nu poate fi îngrijit corespunzător`
    },
    {
      icon: <AlertCircle className="w-6 h-6" />,
      title: "6. Drepturile și responsabilitățile administratorului",
      content: `Administratorul depune eforturi pentru ca informațiile publicate să fie corecte, actuale și accesibile. Totuși, anumite informații pot fi modificate, completate sau retrase atunci când situația de fapt o impune.

Administratorul poate suspenda, limita sau modifica temporar funcționalitățile site-ului pentru mentenanță, securitate, actualizări tehnice sau conformare legală.

Administratorul nu răspunde pentru indisponibilități cauzate de furnizori externi, probleme de rețea, forță majoră sau utilizarea necorespunzătoare a site-ului de către utilizatori.`
    },
    {
      icon: <Scale className="w-6 h-6" />,
      title: "7. Conținut, proprietate intelectuală și linkuri externe",
      content: `Textele, imaginile, elementele grafice, structura site-ului și materialele publicate sunt protejate potrivit legislației aplicabile. Conținutul poate fi consultat pentru informare publică și poate fi distribuit prin link către paginile oficiale.

Nu este permisă copierea, modificarea sau utilizarea materialelor într-un context care poate induce în eroare publicul sau poate afecta drepturile instituției ori ale terților.

Site-ul poate include linkuri către site-uri externe, inclusiv instituții publice, parteneri, InfoCons sau servicii de urgență. Administratorul nu controlează conținutul acestor site-uri externe și nu răspunde pentru politicile lor de confidențialitate sau funcționare.`
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: "8. Protecția datelor și cookie-uri",
      content: `Prelucrarea datelor personale se realizează potrivit Politicii de confidențialitate publicate pe site și potrivit Regulamentului (UE) 2016/679.

Utilizarea cookie-urilor și a tehnologiilor similare este descrisă în Politica privind cookie-urile. Prin folosirea site-ului, utilizatorul confirmă că a fost informat cu privire la aceste politici și poate consulta oricând linkurile dedicate din footer.`
    }
  ];


  return (
    <>
      <SEOHead 
        title="Termeni și Condiții - Protecția Animalelor Sector 1"
        description="Termeni și condiții pentru utilizarea serviciilor Adăpostului Odăi, București. Află regulile pentru adopție, drepturi și responsabilități, politica de returnare și condițiile legale."
        keywords="termeni conditii adaspost, adopție câini termeni, conditii adopție animale, regulament adaspost odai, responsabilități adoptator"
        url="/termeni-conditii"
        type="website"
        noindex={true}
        image="https://images.pexels.com/photos/5668473/pexels-photo-5668473.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
      />
      <PageHeader
        title="Termeni și Condiții"
        subtitle="Regulile și condițiile pentru utilizarea serviciilor noastre"
        image="https://images.pexels.com/photos/5668473/pexels-photo-5668473.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
      />

      <Container>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto"
        >
          <div className="bg-accent-50 border-l-4 border-accent p-6 mb-8 rounded-r-lg">
            <div className="flex items-center mb-3">
              <AlertCircle className="w-6 h-6 text-accent mr-3" />
              <h3 className="text-lg font-bold text-accent">Important</h3>
            </div>
            <p className="text-gray-700">
              Acești termeni și condiții au intrat în vigoare la data de <strong>20 mai 2026</strong>.
              Utilizarea continuă a site-ului constituie acceptarea acestor termeni.
            </p>
          </div>

          <div className="space-y-8">
            {sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-md p-6"
              >
                <div className="flex items-start mb-4">
                  <div className="bg-primary-100 p-3 rounded-full mr-4 flex-shrink-0">
                    <div className="text-primary">
                      {section.icon}
                    </div>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-bold text-primary mb-3 font-display">
                      {section.title}
                    </h2>
                    <div className="text-gray-700 leading-relaxed whitespace-pre-line">
                      {section.content}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.8 }}
            className="bg-primary-100 rounded-lg p-6 mt-8"
          >
            <h2 className="text-xl font-bold text-primary mb-4 font-display">Legea aplicabilă</h2>
            <p className="text-gray-700 mb-4">
              Acești termeni și condiții sunt guvernați de legea română și se supun jurisdicției instanțelor din România.
            </p>
            <div className="text-gray-700 space-y-2">
              <p><strong>Legislație aplicabilă:</strong></p>
              <ul className="list-disc list-inside space-y-1">
                <li>Legea nr. 205/2004 privind protecția animalelor</li>
                <li>O.U.G. nr. 155/2001 privind gestionarea câinilor fără stăpân, acolo unde este aplicabilă</li>
                <li>Regulamentul (UE) 2016/679 privind protecția datelor</li>
                <li>Legea nr. 506/2004 privind comunicațiile electronice</li>
                <li>Codul Civil român</li>
                <li>Legislația privind administrația publică locală și petițiile, acolo unde este aplicabilă</li>
              </ul>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 1.0 }}
            className="bg-white rounded-lg shadow-md p-6 mt-8"
          >
            <h2 className="text-xl font-bold text-primary mb-4 font-display">Sesizări și soluționarea disputelor</h2>
            <p className="text-gray-700 mb-4">
              Orice sesizare, reclamație sau sugestie poate fi transmisă prin canalele oficiale de contact. Solicitările vor fi analizate potrivit termenelor și procedurilor legale aplicabile instituțiilor publice.
            </p>
            <div className="text-gray-700 space-y-3">
              <div>
                <h3 className="font-semibold text-primary mb-1">Soluționare amiabilă:</h3>
                <p>În cazul unei neînțelegeri privind utilizarea site-ului sau a serviciilor publicate, părțile vor încerca mai întâi clarificarea pe cale amiabilă prin canalele oficiale.</p>
              </div>
              <div>
                <h3 className="font-semibold text-primary mb-1">Autorități competente:</h3>
                <p>Pentru aspecte privind datele personale, utilizatorii se pot adresa Responsabilului cu Protecția Datelor sau Autorității Naționale de Supraveghere a Prelucrării Datelor cu Caracter Personal.</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 1.2 }}
            className="bg-white rounded-lg shadow-md p-6 mt-8"
          >
            <h2 className="text-xl font-bold text-primary mb-4 font-display">Contact și reclamații</h2>
            <p className="text-gray-700 mb-4">
              Pentru întrebări despre acești termeni sau pentru a depune o reclamație:
            </p>
            <div className="space-y-2 text-gray-700">
              <p><strong>Email:</strong> {settings?.email || 'protectiaanimalelor@primarias1.ro'}</p>
              <p><strong>DPO:</strong> dpo@primarias1.ro</p>
              <p><strong>Telefon:</strong> {settings?.phone || '+40 750 209 751'}</p>
              <p><strong>Adresa:</strong> {settings?.address || 'Sos. Odăi, nr. 3-5, Sector 1, București'}</p>
            </div>
            <p className="text-gray-700 mt-4 text-sm">
              Solicitările sunt gestionate potrivit termenelor legale aplicabile.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 1.4 }}
            className="bg-gray-50 rounded-lg p-6 mt-8"
          >
            <h2 className="text-xl font-bold text-primary mb-4 font-display">Modificări ale termenilor</h2>
            <p className="text-gray-700">
              Ne rezervăm dreptul de a modifica acești termeni și condiții în orice moment. Modificările vor fi publicate pe această pagină cu o nouă dată de intrare în vigoare. Utilizarea continuă a site-ului după modificări constituie acceptarea noilor termeni.
            </p>
          </motion.div>
        </motion.div>
      </Container>
    </>
  );
};

export default TermsConditions; 
